import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Plus, Edit, Trash2, X, Check, Image, FileText, Link } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ImageUploader from './ImageUploader';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  image: string;
  progress: number;
  isCompleted: boolean;
  videoCount: string;
  tags?: string[];
}

interface ContentManagerProps {
  content?: ContentItem | null;
  sessionId?: string | null;
  bucketName?: string;
  onClose: () => void;
  onContentUpdated: (content: ContentItem) => void;
}

const ContentManager: React.FC<ContentManagerProps> = ({ content, sessionId, bucketName = 'thumbnails_session_1', onClose, onContentUpdated }) => {
  const { user, isAdmin } = useAuth();
  const { showToast } = useToast();
  const [contents, setContents] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados do formulário
  const [isCreating, setIsCreating] = useState(false);
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    videoCount: '1 vídeo',
    tags: ''
  });

  useEffect(() => {
    if (content) {
      setEditingContent(content);
      setIsCreating(false);
      setFormData({
        title: content.title,
        description: content.description,
        image: content.image,
        videoCount: content.videoCount,
        tags: content.tags?.join(', ') || ''
      });
    } else {
      setEditingContent(null);
      setIsCreating(true);
      setFormData({
        title: '',
        description: '',
        image: '',
        videoCount: '1 vídeo',
        tags: ''
      });
    }
  }, [content]);

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      setLoading(true);
      // Aqui você carregaria os conteúdos do banco de dados
      // Por enquanto, vou usar dados mockados
      const mockContents: ContentItem[] = [
        {
          id: 'agregar-produtos',
          title: '1. ¿Cómo agregar',
          description: 'Aprenda como adicionar produtos do mercado de dropshipping à sua loja de forma eficiente.',
          progress: 0,
          image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&h=600',
          isCompleted: false,
          videoCount: '1 vídeo'
        },
        {
          id: 'configurar-transportadora',
          title: '2. ¿Cómo configurar la',
          description: 'Configure a seleção de transportadoras para otimizar seus pedidos e entregas.',
          progress: 0,
          image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&h=600',
          isCompleted: false,
          videoCount: '1 vídeo',
          tags: ['transportadora']
        }
      ];
      
      setContents(mockContents);
    } catch (err) {
      setError('Erro inesperado ao carregar conteúdos');
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      videoCount: '1 vídeo',
      tags: ''
    });
    setIsCreating(false);
    setEditingContent(null);
  };

  const handleCreateContent = () => {
    setIsCreating(true);
    setEditingContent(null);
    resetForm();
  };

  const handleEditContent = (content: ContentItem) => {
    setEditingContent(content);
    setIsCreating(false);
    setFormData({
      title: content.title,
      description: content.description,
      image: content.image,
      videoCount: content.videoCount,
      tags: content.tags?.join(', ') || ''
    });
  };

  const handleSubmit = async () => {
    if (!user || !formData.title.trim()) {
      return;
    }

    try {
      const contentData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        image: formData.image.trim(),
        videoCount: formData.videoCount,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        progress: 0,
        isCompleted: false
      };

      // Aqui você salvaria no banco de dados
      // Por enquanto, vou simular
      if (editingContent) {
        // Atualizar conteúdo existente
        const updatedContent = { ...editingContent, ...contentData };
        onContentUpdated(updatedContent);
      } else {
        // Criar novo conteúdo
        const newContent: ContentItem = {
          id: `content-${Date.now()}`,
          ...contentData
        };
        onContentUpdated(newContent);
      }
      
      onClose();
    } catch (err) {
      setError('Erro inesperado');
      console.error('Erro inesperado:', err);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar o conteúdo'
      });
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      // Aqui você deletaria do banco de dados
      // Por enquanto, vou simular
      const updatedContents = contents.filter(content => content.id !== contentId);
      setContents(updatedContents);
      
      showToast({
        type: 'success',
        title: 'Conteúdo excluído',
        message: 'O conteúdo foi excluído com sucesso!'
      });
      
      onClose();
    } catch (err) {
      setError('Erro inesperado ao excluir conteúdo');
      console.error('Erro inesperado:', err);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao excluir o conteúdo'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0FFF55]"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#121316] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1f2329]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              {editingContent ? 'Editar Conteúdo' : 'Novo Conteúdo'}
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Formulário de criação/edição */}
          <Card className="mb-6 bg-[#1a1d23] border-[#1f2329]">
            <CardHeader>
              <CardTitle className="text-white">
                {editingContent ? 'Editar Conteúdo' : 'Novo Conteúdo'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title" className="text-white">Título</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Título do conteúdo"
                    className="bg-[#0f1114] border-[#1f2329] text-white"
                  />
                </div>
                
                <div>
                  <Label htmlFor="videoCount" className="text-white">Quantidade de Vídeos</Label>
                  <Input
                    id="videoCount"
                    value={formData.videoCount}
                    onChange={(e) => setFormData({ ...formData, videoCount: e.target.value })}
                    placeholder="Ex: 1 vídeo"
                    className="bg-[#0f1114] border-[#1f2329] text-white"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-white">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do conteúdo"
                  rows={3}
                  className="bg-[#0f1114] border-[#1f2329] text-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tags" className="text-white">Tags</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="tag1, tag2, tag3"
                    className="bg-[#0f1114] border-[#1f2329] text-white"
                  />
                </div>
                <div>
                  <ImageUploader
                    bucketName={bucketName}
                    label="Imagem do Conteúdo (Thumbnail) *"
                    value={formData.image}
                    onChange={(url) => setFormData({ ...formData, image: url })}
                    options={{ maxWidth: 800, maxHeight: 600, quality: 0.85, format: 'webp' }}
                    placeholder="https://exemplo.com/imagem.jpg"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-bold py-2.5 px-5 rounded-xl transition-all shadow-md"
                >
                  <Check className="w-4 h-4" />
                  {editingContent ? 'Atualizar' : 'Criar'} Conteúdo
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="text-gray-400 hover:text-white"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContentManager; 